import type {
  AttendanceRecord,
  RollCall,
  RollCallEntry,
} from '@cdf/api-client-react';

export interface ExpectedAttendeesResult {
  /** The immediately preceding roll call (highest sortOrder below the selected one), or null. */
  previousRollCall: RollCall | null;
  /** True when the predecessor has at least one non-pending entry (i.e. has been started). */
  predecessorStarted: boolean;
  /** Student IDs present at the previous roll call who are still checked in. */
  expectedStudentIds: Set<string>;
}

/**
 * Derive the set of students expected at the selected roll call — those marked
 * **present** at the immediately preceding roll call (by `sortOrder`) who are
 * still **checked in** (not signed out since).
 *
 * @param rollCalls           All roll calls for the event (any order).
 * @param selectedRollCallId  The roll call currently being completed.
 * @param predecessorEntries  Entries for the predecessor roll call (already
 *                            fetched by the caller; pass `[]` when there is
 *                            no predecessor or data hasn't loaded yet).
 * @param attendance          Current attendance records for the event.
 */
export function computeExpectedAttendees(
  rollCalls: RollCall[],
  selectedRollCallId: string | null,
  predecessorEntries: RollCallEntry[],
  attendance: AttendanceRecord[],
): ExpectedAttendeesResult {
  const selected = rollCalls.find((rc) => rc.id === selectedRollCallId) ?? null;

  // Find the predecessor: highest sortOrder below the selected one.
  const previousRollCall = selected
    ? [...rollCalls]
        .filter((rc) => rc.sortOrder < selected.sortOrder)
        .sort((a, b) => b.sortOrder - a.sortOrder)[0] ?? null
    : null;

  if (!previousRollCall) {
    return {
      previousRollCall: null,
      predecessorStarted: false,
      expectedStudentIds: new Set<string>(),
    };
  }

  // Has the predecessor been started? (at least one non-pending entry)
  const predecessorStarted = predecessorEntries.some((e) => e.status !== 'pending');

  // Build the set of student IDs still checked in.
  const checkedInIds = new Set(
    attendance
      .filter((a) => a.checkinStatus === 'checked_in')
      .map((a) => a.studentId),
  );

  // Expected = present at previous AND still checked in.
  const expectedStudentIds = new Set(
    predecessorEntries
      .filter((e) => e.status === 'present' && checkedInIds.has(e.studentId))
      .map((e) => e.studentId),
  );

  return { previousRollCall, predecessorStarted, expectedStudentIds };
}
